import { ExtFile, FileInputButton } from "@files-ui/react";
import CheckIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/HighlightOff';
import { Button, DialogActions, DialogContent, Grid, Stack, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useContext, useEffect } from "react";
import { CSVLink } from "react-csv";
import { v4 as uuidv4 } from "uuid";
import { ReactComponent as DownloadIcon } from "../../assets/icons/download.svg";
import awsExports from "../../aws-exports";
import { AuthContext } from "../../contexts/AuthContext";
import { s3UploadToExternalBucket } from "../../helpers/s3";
import useS3FileUploader from "../../hooks/useS3FileUploader";
import theme from "../../theme";
import { makeId } from "../../utils/makeId";
import CircularProgressPercent from "./CircularProgressPercent";

const useStyles = makeStyles((theme) => ({
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        padding: 8,
        height: '100%',
        width: '100%',
        overflowY: 'auto',
    },
    dialogActions: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: 16,
    },
    text: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        color: '#666666',
    },
    tableHeader: {
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 600,
        color: '#666666',
        padding: '8px 8px',
        border: `1px solid ${theme.colors.neutral['300']}`,
    },
    tableRowExample: {
        fontSize: '1rem',
        color: theme.colors.neutral['600'],
        padding: '8px 8px',
        border: `1px solid ${theme.colors.neutral['300']}`,
    },
    donwloadIcon: {
        width: 20,
        height: 20,
        stroke: theme.palette.primary.light,
    },
    downloadLink: {
        textDecoration: 'none',
        "&:hover": {
            textDecoration: 'none !important',
        }
    },
    downloadText: {
        fontSize: '1rem',
        color: theme.palette.primary.light,
    },
    uploadButton: {
        width: 150,
        height: 40,
        borderRadius: 20,
        backgroundColor: `${theme.palette.primary.main} !important`,
        color: 'white',
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 'bold !important',
        textTransform: 'none !important' as any,
        transition: 'ease-in-out 300ms',
    },
    closeButton: {
        width: 100,
        height: 36,
        borderRadius: 20,
        fontSize: '0.95rem',
        fontWeight: 'bold !important',
        textTransform: 'none !important' as any,
        transition: 'ease-in-out 300ms',
    },
}));

const { aws_dashboard_bulk_uploads_bucket } = awsExports;

const BULK_ADD_VALID_FILE = { maxFileSize: 100 * 1024 * 1024, allowedFileTypes: ['text/csv'], };

const BulkAddDashboards: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const classes = useStyles();
    const { userGroup } = useContext(AuthContext);

    const { uploading, progress, hasUploaded, uploadFiles, resetUploader } = useS3FileUploader(
        (extFile: ExtFile) => s3UploadToExternalBucket({
            path: ['dashboards', userGroup, `${uuidv4()}.csv`].join('/'),
            file: extFile.file,
            bucket: aws_dashboard_bulk_uploads_bucket,
        })
    );

    useEffect(() => {
        resetUploader();
    // eslint-disable-next-line
    }, []);

    return (<>
        {uploading ? (<>
            <Stack alignItems="center" justifyContent="center" width="100%" height="100%" py={10}>
                <CircularProgressPercent value={progress} />
            </Stack>
        </>) : hasUploaded ? (<>
            <DialogContent className={classes.dialogContent}>
                <Stack alignItems="center" justifyContent="center" width="100%" height="100%" py={8}>
                    <CheckIcon sx={{ fontSize: '3rem', color: theme.palette.primary.main }} />
                    <Typography variant="body1" color="black" fontWeight="bold" fontSize="1.25rem !important"
                        sx={{ marginTop: '20px !important' }}>
                        {'Saved to Notissia'}
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button variant="outlined"
                    className={classes.closeButton}
                    onClick={onClose}>{'Close'}</Button>
            </DialogActions>
        </>) : (progress > 0) ? (<>
            <DialogContent className={classes.dialogContent}>
                <Stack alignItems="center" justifyContent="center" width="100%" height="100%" py={8}>
                    <CancelIcon sx={{ fontSize: '3rem', color: 'red' }}  />
                    <Typography variant="body1" color="black" fontWeight="bold" fontSize="1.25rem !important"
                        sx={{ marginTop: '20px !important' }}>
                        {'File upload error!'}
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button variant="outlined"
                    className={classes.closeButton}
                    onClick={onClose}>{'Close'}</Button>
            </DialogActions>
        </>) : (<>
            <DialogContent className={classes.dialogContent}>
                <Stack spacing={2} alignItems="flex-start" justifyContent="center" my={1}>
                    <Stack>
                        <Typography className={classes.text}>
                            {`Add multiple companies by uploading a CSV in the following format. `}
                        </Typography>
                        <Typography className={classes.text}>
                            {`Company name and website are required. Optional fields include `}
                        </Typography>
                        <Typography className={classes.text}>
                            {`Stage, Source, and Valuation.`}
                        </Typography>
                    </Stack>
                    <Stack spacing={1} alignItems="center">
                        <Grid container width="50%">
                            <Grid item className={classes.tableHeader} xs={6} sx={{ borderTopLeftRadius: 16 }}>
                                {'Name'}
                            </Grid>
                            <Grid item className={classes.tableHeader} xs={6} sx={{ borderTopRightRadius: 16 }}>
                                {'Website'}
                            </Grid>
                            <Grid item className={classes.tableRowExample} xs={6}>
                                {'Company A'}
                            </Grid>
                            <Grid item className={classes.tableRowExample} xs={6}>
                                {'compA.com'}
                            </Grid>
                            <Grid item className={classes.tableRowExample} xs={6} sx={{ borderBottomLeftRadius: 16 }}>
                                {'Company B'}
                            </Grid>
                            <Grid item className={classes.tableRowExample} xs={6} sx={{ borderBottomRightRadius: 16 }}>
                                {'compB.com'}
                            </Grid>
                        </Grid>
                        <CSVLink uFEFF={false} className={classes.downloadLink}
                            headers={["Name", "Website", "Stage", "Valuation", "Source"]} data={[]}
                            filename={`list-of-companies-template-${makeId(5)}.csv`}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <DownloadIcon className={classes.donwloadIcon} />
                                <Typography className={classes.downloadText}>
                                    {'Download CSV template'}
                                </Typography>
                            </Stack>
                        </CSVLink>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                {!uploading && (
                    <FileInputButton
                        className={classes.uploadButton}
                        onChange={uploadFiles}
                        maxFileSize={BULK_ADD_VALID_FILE.maxFileSize}
                        accept={BULK_ADD_VALID_FILE.allowedFileTypes.join(',')}
                        behaviour="replace"
                        label="Upload CSV"
                    />
                )}
            </DialogActions>
        </>)}
    </>);
}
export default BulkAddDashboards;